import { useRef, useState } from 'react';
import jersey from '@fontsource/jersey-10/files/jersey-10-latin-400-normal.woff2?url';
import { LONG_NIGHT, type ChainKeepsake, type NightRun } from '../../../campaign';
import { t } from '../../../i18n';
import { REGULAR_SMOKE, SMOKE, type Rgb, type SmokeName } from '../../scene';
import { Modal } from '../Modal';
import { formatScore } from '../ScoreBurst';
import { Emblem } from './Emblem';
import { nightLabel, tableKindName } from './runText';

/**
 * The share card (docs/LONG_NIGHT_ART_DIRECTION.md §11.23): 1080 × 1350, drawn from SVG in the
 * browser. At the top, PAWNED · The Long Night in the game's pixel face, with the Night, the table and the
 * date or seed in small capitals; in the middle a small board with the chain's route drawn as
 * light, the Tally as a strip of receipt, and the Mantel in a row; at the bottom the score in gold
 * numerals, large enough to read as a thumbnail. Behind, a still frame of the smoke in the palette
 * of the table where it happened.
 */

const W = 1080;
const H = 1350;
const BOARD = { x: 240, y: 250, size: 600 };

const css = (c: Rgb, a = 1): string =>
  `rgba(${Math.round(c[0] * 255)}, ${Math.round(c[1] * 255)}, ${Math.round(c[2] * 255)}, ${a})`;

function smokeOf(k: ChainKeepsake | undefined, run: NightRun): SmokeName {
  const night = k?.night ?? run.night;
  if (k?.kind === 'high' && k.regular !== undefined) return REGULAR_SMOKE[k.regular] ?? 'void';
  if (night <= 1) return 'void';
  if (night === 2) return 'night2';
  if (night === 3) return 'night3';
  return night === LONG_NIGHT.NIGHTS + 1 ? 'closing' : 'afterHours';
}

/** A square's middle on the card's board, South at the bottom. */
const at = (sq: { x: number; y: number }) => ({
  x: BOARD.x + (sq.x + 0.5) * (BOARD.size / 6),
  y: BOARD.y + (5 - sq.y + 0.5) * (BOARD.size / 6),
});

export function ShareCardSvg({ run }: { run: NightRun }) {
  const k = run.bestKeepsake;
  const p = SMOKE[smokeOf(k, run)];
  const score = k?.score ?? run.bestChain;
  const route = (k?.path ?? []).map(at);
  const where = [
    nightLabel(k?.night ?? run.night),
    k ? tableKindName(k.kind) : '',
    run.daily ?? run.seed,
  ]
    .filter(Boolean)
    .join(' · ')
    .toUpperCase();
  const mantel = run.mantel.slice(0, 5);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      className="share-card"
    >
      <defs>
        <filter id="sc-blur" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="90" />
        </filter>
        <filter id="sc-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="9" />
        </filter>
        <linearGradient id="sc-gold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff1c2" />
          <stop offset="0.5" stopColor="#ffcf5a" />
          <stop offset="1" stopColor="#b8871f" />
        </linearGradient>
      </defs>
      {/* A still frame of the table's smoke. */}
      <rect width={W} height={H} fill={css(p.void)} />
      <g filter="url(#sc-blur)">
        <ellipse cx="300" cy="380" rx="420" ry="300" fill={css(p.body, 0.75)} />
        <ellipse cx="820" cy="760" rx="380" ry="320" fill={css(p.current, 0.7)} />
        <ellipse cx="420" cy="1080" rx="460" ry="240" fill={css(p.deep, 0.9)} />
        <ellipse cx="760" cy="300" rx="200" ry="140" fill={css(p.wisp, 0.18)} />
      </g>

      <text
        x={W / 2}
        y="128"
        textAnchor="middle"
        fontFamily="'Jersey 10', sans-serif"
        fontSize="72"
        fill="#f6e7c8"
      >
        PAWNED · {t('night.title')}
      </text>
      <text
        x={W / 2}
        y="184"
        textAnchor="middle"
        fontFamily="'Jersey 10', sans-serif"
        fontSize="24"
        letterSpacing="5"
        fill="rgba(216, 207, 232, 0.8)"
      >
        {where}
      </text>

      {/* The board, with the chain's route drawn as light. */}
      <rect
        x={BOARD.x - 14}
        y={BOARD.y - 14}
        width={BOARD.size + 28}
        height={BOARD.size + 28}
        rx="26"
        fill="rgba(8, 6, 20, 0.72)"
        stroke="rgba(255, 207, 138, 0.45)"
        strokeWidth="2"
      />
      {Array.from({ length: 36 }, (_, i) => {
        const x = i % 6;
        const y = Math.floor(i / 6);
        return (
          <rect
            key={i}
            x={BOARD.x + x * 100 + 3}
            y={BOARD.y + y * 100 + 3}
            width="94"
            height="94"
            rx="10"
            fill={(x + y) % 2 ? 'rgba(36, 28, 74, 0.8)' : 'rgba(24, 18, 52, 0.8)'}
          />
        );
      })}
      {route.length > 1 && (
        <>
          <polyline
            points={route.map((r) => `${r.x},${r.y}`).join(' ')}
            fill="none"
            stroke="#ffcf8a"
            strokeWidth="22"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.55"
            filter="url(#sc-glow)"
          />
          <polyline
            points={route.map((r) => `${r.x},${r.y}`).join(' ')}
            fill="none"
            stroke="#fff3dc"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle
            cx={route[0]!.x}
            cy={route[0]!.y}
            r="26"
            fill="none"
            stroke="#fff3dc"
            strokeWidth="4"
          />
          {route.slice(1).map((r, i) => (
            <circle key={i} cx={r.x} cy={r.y} r="11" fill="#ffcf8a" />
          ))}
        </>
      )}

      {/* The Tally, as a strip of receipt. */}
      {k && (
        <g transform="translate(240 900)">
          <rect width="600" height="74" fill="#efe4cc" />
          <text
            x="300"
            y="49"
            textAnchor="middle"
            fontFamily="'Jersey 10', sans-serif"
            fontSize="32"
            fill="#2a1d12"
          >
            {`${k.points} P  ×  ${k.multiplier} M  =  ${formatScore(k.score)}`}
          </text>
        </g>
      )}

      {/* The Mantel, in a row. */}
      <g transform={`translate(${W / 2 - (mantel.length * 110) / 2 + 10} 1000)`}>
        {mantel.map((item, i) => (
          <g key={item.uid} transform={`translate(${i * 110} 0)`}>
            <rect width="90" height="90" rx="12" fill="rgba(0, 0, 0, 0.35)" />
            <g transform="translate(9 9)">
              <Emblem name={item.id} size={72} />
            </g>
          </g>
        ))}
      </g>

      {/* The score, in gold numerals at least 12% of the card's height. */}
      <text
        x={W / 2}
        y="1290"
        textAnchor="middle"
        fontFamily="'Jersey 10', sans-serif"
        fontSize="190"
        fill="url(#sc-gold)"
      >
        {formatScore(score)}
      </text>
    </svg>
  );
}

/** The card's font, embedded so the picture keeps its pixel face once it leaves the page. */
async function fontFaces(): Promise<string> {
  const embed = async (url: string) => {
    const buf = await (await fetch(url)).arrayBuffer();
    let bin = '';
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
    return `data:font/woff2;base64,${btoa(bin)}`;
  };
  return `@font-face{font-family:'Jersey 10';src:url(${await embed(jersey)}) format('woff2')}`;
}

/** Draws the card's SVG into a 1080 × 1350 PNG. */
async function cardPng(svg: SVGSVGElement): Promise<Blob> {
  const copy = svg.cloneNode(true) as SVGSVGElement;
  const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
  style.textContent = await fontFaces().catch(() => '');
  copy.insertBefore(style, copy.firstChild);
  const text = new XMLSerializer().serializeToString(copy);
  const img = new Image();
  img.decoding = 'async';
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(text)}`;
  await img.decode();
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  canvas.getContext('2d')!.drawImage(img, 0, 0, W, H);
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('no image'))), 'image/png'),
  );
}

/** The share card's sheet: the card, and a way to share or save it as a picture. */
export function ShareSheet({ run, onClose }: { run: NightRun; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    const svg = ref.current?.querySelector('svg');
    if (!svg) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await cardPng(svg);
      const file = new File([blob], `pawned-long-night-${run.number}.png`, { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: 'PAWNED' });
      } else {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = file.name;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      }
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) setError(t('share.cardFailed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={t('share.cardTitle')}
      onClose={onClose}
      className="ln-sheet share-sheet"
      footer={
        <button
          type="button"
          className="btn btn-primary btn-brass"
          disabled={busy}
          onClick={() => void go()}
        >
          {t('share.cardShare')}
        </button>
      }
    >
      <div ref={ref} className="share-preview">
        <ShareCardSvg run={run} />
      </div>
      {error && <p className="ln-sheet-note">{error}</p>}
    </Modal>
  );
}
